import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clinicianService, userService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import SOSButton from '../components/SOSButton';
import HomeworkAssignmentModal from '../components/HomeworkAssignmentModal';
import { Badge, Button, Card, Input } from '../components/ui';
import { openMomentumInviteEmail } from '../utils/inviteEmail';

const phaseNames = ['Stabilize', 'Understand', 'Rebuild', 'Sustain'];

const statusColors = {
  Consistent: 'border-l-success',
  Inconsistent: 'border-l-warning',
  Away: 'border-l-error'
};

const LICENSE_TYPE_OPTIONS = [
  'LPC',
  'LPC-S',
  'LCSW',
  'LMFT',
  'LMFT-S',
  'LCDC',
  'PsyD',
  'Psychiatrist',
  'Psychologist',
  'MD',
  'PA',
  'NP',
  'Life Coach'
];

const formatDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const buildCredentialForm = (user) => ({
  city: user?.city || '',
  state: user?.state || '',
  ordinationChurch: user?.ordinationChurch || '',
  ordinationYear: user?.ordinationYear || '',
  licenses: (user?.licenses || []).map((license) => ({
    licenseNumber: license?.licenseNumber || '',
    licenseType: license?.licenseType || '',
    licenseState: license?.licenseState || '',
    licenseExpiration: formatDateInput(license?.licenseExpiration)
  }))
});

const toIsoDate = (value) => (value ? new Date(value).toISOString() : '');

const ClergyDashboard = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [stats, setStats] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [showHomeworkModal, setShowHomeworkModal] = useState(false);
  const [credentialForm, setCredentialForm] = useState(() => buildCredentialForm(user));
  const [credentialMessage, setCredentialMessage] = useState(null);
  const [credentialSaving, setCredentialSaving] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsResponse, statsResponse, promptsResponse] = await Promise.all([
          clinicianService.getClients(),
          clinicianService.getDashboardStats(),
          clinicianService.getEncouragementPrompts()
        ]);

        setParticipants(clientsResponse.data?.data?.clients || []);
        setStats(statsResponse.data?.data?.stats || null);
        setPrompts(promptsResponse.data?.data?.prompts || []);
      } catch (error) {
        setMessage({
          type: 'error',
          text: error.response?.data?.message || 'Failed to load clergy dashboard.'
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    setCredentialForm(buildCredentialForm(user));
  }, [user]);

  const engagementSummary = useMemo(() => {
    if (!stats?.engagement) return null;
    return [
      { label: 'Consistent', value: stats.engagement.consistent || 0 },
      { label: 'Inconsistent', value: stats.engagement.inconsistent || 0 },
      { label: 'Away', value: stats.engagement.away || 0 }
    ];
  }, [stats]);

  if (loading) {
    return <LoadingSpinner message="Loading clergy dashboard..." />;
  }

  const handleCredentialChange = (field, value) => {
    setCredentialForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdditionalLicenseChange = (index, field, value) => {
    setCredentialForm((prev) => {
      const updatedLicenses = [...prev.licenses];
      updatedLicenses[index] = {
        ...updatedLicenses[index],
        [field]: value
      };
      return {
        ...prev,
        licenses: updatedLicenses
      };
    });
  };

  const handleAddLicense = () => {
    setCredentialForm((prev) => ({
      ...prev,
      licenses: [
        ...prev.licenses,
        {
          licenseNumber: '',
          licenseType: '',
          licenseState: '',
          licenseExpiration: ''
        }
      ]
    }));
  };

  const handleRemoveLicense = (index) => {
    setCredentialForm((prev) => ({
      ...prev,
      licenses: prev.licenses.filter((_, licenseIndex) => licenseIndex !== index)
    }));
  };

  const handleSaveCredentials = async () => {
    setCredentialMessage(null);

    const hasPartialLicense = credentialForm.licenses.some((license) => {
      const fields = [license.licenseType, license.licenseNumber, license.licenseState, license.licenseExpiration];
      const filled = fields.filter(Boolean).length;
      return filled > 0 && filled < fields.length;
    });

    if (hasPartialLicense) {
      setCredentialMessage({
        type: 'error',
        text: 'Complete all fields for each additional license or remove the entry.'
      });
      return;
    }

    setCredentialSaving(true);
    try {
      const payload = {
        city: credentialForm.city.trim(),
        state: credentialForm.state.trim().toUpperCase(),
        ordinationChurch: credentialForm.ordinationChurch.trim(),
        ordinationYear: credentialForm.ordinationYear ? Number(credentialForm.ordinationYear) : null,
        licenses: credentialForm.licenses
          .filter((license) => license.licenseType && license.licenseNumber && license.licenseState && license.licenseExpiration)
          .map((license) => ({
            licenseNumber: license.licenseNumber.trim(),
            licenseType: license.licenseType,
            licenseState: license.licenseState.trim().toUpperCase(),
            licenseExpiration: toIsoDate(license.licenseExpiration)
          }))
      };

      const response = await userService.updateProfile(user.id, payload);
      updateUser(response.data.data.user);
      setCredentialMessage({ type: 'success', text: 'Credentials updated successfully.' });
    } catch (error) {
      setCredentialMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to update credentials.'
      });
    } finally {
      setCredentialSaving(false);
    }
  };

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <Badge variant="secondary" size="sm">Clergy Console</Badge>
            <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">Participant Care</h2>
            <p className="mt-2 text-neutral-600">
              Engagement signals and encouragement tools without clinical indicators.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => openMomentumInviteEmail({
                inviterName: `${user.firstName} ${user.lastName}`,
                invitationType: 'clergy',
                ministryName: user.ordinationChurch
              })}
            >
              Send Invite Email
            </Button>
            <Button variant="primary" onClick={() => setShowHomeworkModal(true)}>
              Assign Homework
            </Button>
            <Button variant="primary" onClick={() => navigate('/clinician/messages')}>
              Send Encouragement
            </Button>
          </div>
        </div>

        {message && (
          <div
            className={`mb-6 rounded-soft border p-3 text-sm ${
              message.type === 'success'
                ? 'border-success-200 bg-success-50 text-success-700'
                : 'border-error-200 bg-error-50 text-error-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <p className="text-sm font-medium text-neutral-500">Participants</p>
            <p className="mt-2 text-2xl font-semibold text-neutral-900">
              {stats?.totalParticipants || participants.length}
            </p>
          </Card>
          <Card>
            <p className="text-sm font-medium text-neutral-500">Milestones this week</p>
            <p className="mt-2 text-2xl font-semibold text-neutral-900">
              {stats?.milestonesThisWeek || 0}
            </p>
          </Card>
          <Card>
            <p className="text-sm font-medium text-neutral-500">Messages sent (7 days)</p>
            <p className="mt-2 text-2xl font-semibold text-neutral-900">
              {stats?.messagesSent || 0}
            </p>
          </Card>
        </div>

        <Card className="mt-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-display font-semibold text-neutral-900">Credentials</h3>
              <p className="text-sm text-neutral-600">Update ordination details and optional professional licenses.</p>
            </div>
            <Button
              variant="secondary"
              onClick={handleSaveCredentials}
              disabled={credentialSaving}
            >
              {credentialSaving ? 'Saving...' : 'Save Credentials'}
            </Button>
          </div>

          {credentialMessage && (
            <div
              className={`mt-4 rounded-soft border p-3 text-sm ${
                credentialMessage.type === 'success'
                  ? 'border-success-200 bg-success-50 text-success-700'
                  : 'border-error-200 bg-error-50 text-error-700'
              }`}
            >
              {credentialMessage.text}
            </div>
          )}

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Input
              label="Church of Ordination"
              value={credentialForm.ordinationChurch}
              onChange={(event) => handleCredentialChange('ordinationChurch', event.target.value)}
              fullWidth
            />
            <Input
              label="Year Ordained"
              type="number"
              value={credentialForm.ordinationYear}
              onChange={(event) => handleCredentialChange('ordinationYear', event.target.value)}
              fullWidth
            />
            <Input
              label="City"
              value={credentialForm.city}
              onChange={(event) => handleCredentialChange('city', event.target.value)}
              fullWidth
            />
            <Input
              label="State (2-letter code)"
              value={credentialForm.state}
              onChange={(event) => handleCredentialChange('state', event.target.value)}
              fullWidth
            />
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between gap-4">
              <h4 className="text-sm font-semibold text-neutral-800">Professional Licenses (Optional)</h4>
              <Button size="sm" variant="ghost" onClick={handleAddLicense}>Add License</Button>
            </div>
            <div className="mt-3 space-y-4">
              {credentialForm.licenses.length === 0 && (
                <p className="text-xs text-neutral-500">No additional licenses added.</p>
              )}
              {credentialForm.licenses.map((license, index) => (
                <div key={`license-${index}`} className="rounded-soft border border-neutral-200 p-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      label="License Number"
                      value={license.licenseNumber}
                      onChange={(event) => handleAdditionalLicenseChange(index, 'licenseNumber', event.target.value)}
                      fullWidth
                    />
                    <label className="text-sm text-neutral-700">
                      <span className="mb-2 block font-medium">License Type</span>
                      <select
                        value={license.licenseType}
                        onChange={(event) => handleAdditionalLicenseChange(index, 'licenseType', event.target.value)}
                        className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                      >
                        <option value="">Select license type</option>
                        {LICENSE_TYPE_OPTIONS.map((type) => (
                          <option key={`${type}-${index}`} value={type}>{type}</option>
                        ))}
                      </select>
                    </label>
                    <Input
                      label="License State"
                      value={license.licenseState}
                      onChange={(event) => handleAdditionalLicenseChange(index, 'licenseState', event.target.value)}
                      fullWidth
                    />
                    <Input
                      label="License Expiration"
                      type="date"
                      value={license.licenseExpiration}
                      onChange={(event) => handleAdditionalLicenseChange(index, 'licenseExpiration', event.target.value)}
                      fullWidth
                    />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveLicense(index)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {engagementSummary && (
          <Card className="mt-6">
            <h3 className="text-lg font-display font-semibold text-neutral-800">Engagement Overview</h3>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              {engagementSummary.map((item) => (
                <div key={item.label} className="rounded-soft border border-neutral-200 p-4">
                  <p className="text-sm text-neutral-500">{item.label}</p>
                  <p className="mt-2 text-2xl font-semibold text-neutral-900">{item.value}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Messaging Guidance */}
        <div className="mt-6 rounded-lg border-2 border-primary-200 bg-primary-50 p-5">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-primary-900">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Encouragement Message Guidance
          </h3>
          <div className="mt-3 space-y-2 text-sm text-primary-800">
            <p className="font-medium">
              Focus your encouragement messages on:
            </p>
            <ul className="ml-5 space-y-1 list-disc">
              <li><strong>Platform engagement:</strong> Encourage daily login and content completion</li>
              <li><strong>Consistency:</strong> Emphasize the mental health benefits of regular practice</li>
              <li><strong>Observable progress:</strong> Acknowledge streaks, milestones, and phase transitions</li>
              <li><strong>Long-term commitment:</strong> Reinforce that consistent use leads to lasting mental health gains</li>
            </ul>
            <p className="mt-3 text-xs text-primary-700 italic">
              💡 Remember: These messages are about motivating platform use, not spiritual counseling. 
              Encourage participants to build the habit of daily engagement for maximum therapeutic benefit.
            </p>
          </div>
        </div>

        <Card className="mt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-display font-semibold text-neutral-800">Milestone Prompts</h3>
            <Badge variant="secondary">{prompts.length} ready</Badge>
          </div>
          <div className="mt-4 space-y-3">
            {prompts.map((prompt) => (
              <div key={`${prompt.client._id}-${prompt.template.templateId}`} className="rounded-soft border border-neutral-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-neutral-900">
                      {prompt.client.firstName} {prompt.client.lastName}
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">{prompt.reason}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const params = new URLSearchParams({
                        clientId: prompt.client._id,
                        templateId: prompt.template.templateId
                      });
                      if (prompt.triggerData?.dayNumber) {
                        params.set('dayNumber', String(prompt.triggerData.dayNumber));
                      }
                      if (prompt.triggerData?.timePeriod) {
                        params.set('timePeriod', prompt.triggerData.timePeriod);
                      }
                      navigate(`/clinician/messages?${params.toString()}`);
                    }}
                  >
                    Send
                  </Button>
                </div>
              </div>
            ))}
            {prompts.length === 0 && (
              <p className="text-sm text-neutral-500">No milestone prompts available right now.</p>
            )}
          </div>
        </Card>

        <Card className="mt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-display font-semibold text-neutral-800">Participant Roster</h3>
            <Badge variant="secondary">{participants.length} total</Badge>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="border-b border-neutral-200 text-neutral-500">
                <tr>
                  <th className="py-3 pr-4 font-medium">Participant</th>
                  <th className="py-3 pr-4 font-medium">Username</th>
                  <th className="py-3 pr-4 font-medium">Email</th>
                  <th className="py-3 pr-4 font-medium">Phone</th>
                  <th className="py-3 pr-4 text-center font-medium">Invited</th>
                  <th className="py-3 pr-4 text-center font-medium">Profile Created</th>
                  <th className="py-3 pr-4 font-medium">Day & Phase</th>
                  <th className="py-3 pr-4 font-medium">Streak</th>
                  <th className="py-3 pr-4 font-medium">Last Active</th>
                  <th className="py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {participants.map((participant) => (
                  <tr key={participant.id} className="hover:bg-neutral-50">
                    <td className="py-3 pr-4 text-neutral-900">
                      {participant.firstName} {participant.lastName}
                    </td>
                    <td className="py-3 pr-4 text-neutral-600">{participant.username || 'N/A'}</td>
                    <td className="py-3 pr-4 text-neutral-600">{participant.email || 'N/A'}</td>
                    <td className="py-3 pr-4 text-neutral-600">{participant.phone || 'N/A'}</td>
                    <td className="py-3 pr-4 text-center">
                      {participant.invitationSentAt ? (
                        <Badge variant="success" size="sm">Yes</Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">No</Badge>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-center">
                      {participant.isVerified && participant.lastLogin ? (
                        <Badge variant="success" size="sm">Yes</Badge>
                      ) : (
                        <Badge variant="warning" size="sm">Pending</Badge>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-neutral-600">
                      Day {participant.currentDay} — {phaseNames[(participant.currentPhase || 1) - 1]}
                    </td>
                    <td className="py-3 pr-4 text-neutral-600">
                      {participant.streak || 0} days
                    </td>
                    <td className="py-3 pr-4 text-neutral-600">
                      {participant.lastActive ? new Date(participant.lastActive).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-3">
                      <span className={`inline-flex items-center gap-2 rounded-full border-l-4 px-3 py-1 text-xs font-semibold text-neutral-700 ${statusColors[participant.engagementStatus] || ''}`}>
                        {participant.engagementStatus}
                      </span>
                    </td>
                  </tr>
                ))}
                {participants.length === 0 && (
                  <tr>
                    <td colSpan={10} className="py-6 text-center text-neutral-500">
                      No participants found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="mt-6">
          <h3 className="text-lg font-display font-semibold text-neutral-800">Phase Context</h3>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="rounded-soft border border-neutral-200 p-4">
              <p className="text-sm font-semibold text-neutral-900">Phase 1: Stabilize</p>
              <p className="mt-2 text-sm text-neutral-600">
                Participants are building daily structure and learning foundational coping skills. Consistent encouragement matters most here.
              </p>
            </div>
            <div className="rounded-soft border border-neutral-200 p-4">
              <p className="text-sm font-semibold text-neutral-900">Phase 2: Understand</p>
              <p className="mt-2 text-sm text-neutral-600">
                Participants explore patterns and triggers. Your steady presence helps them stay grounded through hard insights.
              </p>
            </div>
            <div className="rounded-soft border border-neutral-200 p-4">
              <p className="text-sm font-semibold text-neutral-900">Phase 3: Rebuild</p>
              <p className="mt-2 text-sm text-neutral-600">
                Participants apply skills in daily life. Encouragement reinforces their new routines and resilience.
              </p>
            </div>
            <div className="rounded-soft border border-neutral-200 p-4">
              <p className="text-sm font-semibold text-neutral-900">Phase 4: Sustain</p>
              <p className="mt-2 text-sm text-neutral-600">
                Participants integrate long-term practices. Celebrate milestones and reinforce their forward momentum.
              </p>
            </div>
          </div>
        </Card>

        <Card className="mt-6">
          <h3 className="text-lg font-display font-semibold text-neutral-800">Referral Prompt</h3>
          <p className="mt-2 text-sm text-neutral-600">
            If someone has been away for 7+ days, consider a gentle outreach. If you are concerned about their wellbeing, remind them to
            connect with their clinician or call 988.
          </p>
        </Card>
      </main>

      <SOSButton variant="floating" />

      <HomeworkAssignmentModal
        isOpen={showHomeworkModal}
        onClose={() => setShowHomeworkModal(false)}
        clients={participants.map((participant) => ({
          _id: participant._id || participant.id,
          firstName: participant.firstName,
          lastName: participant.lastName,
          email: participant.email,
          currentDay: participant.currentDay,
        }))}
        onAssignSuccess={() => setMessage({ type: 'success', text: 'Homework assigned successfully.' })}
      />
    </>
  );
};

export default ClergyDashboard;
